import { useMemo } from 'react';

import {
  NUMBER_OF_RELATED_ARTICLES_TO_DISPLAY,
  RELATED_ARTICLES_INDEX_NAME,
} from 'bundles/unified-seo-content-common/constants';
import useAlgoliaQueryCSR from 'bundles/unified-seo-content-common/hooks/useAlgoliaQueryCSR';
import type { SearchParameters } from 'bundles/unified-seo-content-common/hooks/useAlgoliaQueryCSR';
import { extractSlugFromArticlesAlgoliaObjectUrl } from 'bundles/unified-seo-content-common/utils';

export type ArticleMetadata = {
  slug: string;
  title: string;
  targetKeyword?: string | null;
  category: string;
  subcategory?: string | null;
  localizedCountryCode: string;
};

type RelatedArticleHit = {
  objectUrl: string;
  name: string;
  localizedCountryCd: string;
  localizedLanguageCd: string;
  lastPublishedAt: string;
};

const useRelatedArticlesData = ({
  targetKeyword,
  title,
  category,
  subcategory,
  localizedCountryCode,
  slug,
}: ArticleMetadata) => {
  const memoizedSearchParameters = useMemo(
    (): SearchParameters => ({
      query: `"${targetKeyword ?? title} ${subcategory ?? category}"`,
      removeWordsIfNoResults: 'allOptional',
      filters: `category:"${category}" AND localizedCountryCd:${localizedCountryCode} AND NOT objectUrl:/articles/${slug}`,
      optionalWords: [targetKeyword ?? title, subcategory ?? category],
      hitsPerPage: NUMBER_OF_RELATED_ARTICLES_TO_DISPLAY,
    }),
    [slug, title, targetKeyword, category, subcategory, localizedCountryCode]
  );

  const { results, searching } = useAlgoliaQueryCSR<RelatedArticleHit>(
    RELATED_ARTICLES_INDEX_NAME,
    memoizedSearchParameters
  );

  return {
    relatedArticles: results.map((result) => ({
      slug: extractSlugFromArticlesAlgoliaObjectUrl(result.objectUrl),
      title: result.name,
      localizedCountryCode: result.localizedCountryCd,
      localizedLanguageCode: result.localizedLanguageCd,
      publishedAt: result.lastPublishedAt,
    })),
    loadingRelatedArticles: searching,
  };
};

export default useRelatedArticlesData;
